import { Injectable, TemplateRef } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { LazyLoadEvent } from 'primeng/api';
import { ColumnOutputType } from '../../component-library/table/column-output-type';
import { TableColumn } from '../../component-library/table/table-column';
import { TableConfig } from '../../component-library/table/table-config';
import { TableItemState } from '../../component-library/table/table-item-state.enum';
import { TableConfigHelperService } from '../../shared/table-helpers/table-config-helper-service';
import { RowsPerPage, defaultIndexColumnWidth, defaultRowsPerPageOptions } from '../../shared/table-helpers/table-helper';
import { HierarchyTemplateAttribute } from '../../viewmodels/admin/hierarchy-templates/hierarchy-template-attribute';

@Injectable({
  providedIn: 'root',
})
export class HierarchyTemplateAttributesTableConfigService {
  public attributesConfig: TableConfig;

  public readonly rowNumberColumnName = 'rowNumber';

  public constructor(private i18NextPipe: I18NextPipe, private tableConfigHelperService: TableConfigHelperService) { }

  public initAttributesTableConfig(
    typeTemplateRef: TemplateRef<any>,
    uomTemplateRef: TemplateRef<any>,
    lockedTemplateRef: TemplateRef<any>,
    nameTemplateRef: TemplateRef<any>,
  ): void {
    const config = this.createBaseTableConfig();

    config.id = 'attributes-table';
    config.title = this.i18NextPipe.transform('HierarchyTemplateAttributeList.Attributes');
    config.columns = this.createAttributeColumns(typeTemplateRef, uomTemplateRef, lockedTemplateRef, nameTemplateRef);
    config.startingSortField = this.rowNumberColumnName;
    config.itemTypeName = this.i18NextPipe.transform('HierarchyTemplateAttributeList.Attribute');
    config.deleteActionTitle = this.i18NextPipe.transform('HierarchyTemplateAttributes.Delete');
    config.rowsPerPage = RowsPerPage.Large;
    config.rowsPerPageOptions = [...defaultRowsPerPageOptions, RowsPerPage.Massive];
    config.lazyLoading = true;
    config.lazyLoadOnInit = false;
    config.actions.push(this.tableConfigHelperService.createDeleteTableAction(config.deleteActionTitle, true));
    config.showRowHighlightInfoColumn = false;
    config.rowHighlightFunction = rowData => this.setRowStyleClass(rowData);
    config.totalRecords = 0;
    config.reorderableRow = true;
    config.loading = false;
    config.enablePagination = true;
    this.attributesConfig = config;
  }

  private setRowStyleClass(rowData: HierarchyTemplateAttribute): string {
    return rowData.state === TableItemState.Added || rowData.state === TableItemState.Modified ? 'row-default-warning' : null;
  }

  private createAttributeColumns(
    typeTemplateRef: TemplateRef<any>,
    uomTemplateRef: TemplateRef<any>,
    lockedTemplateRef: TemplateRef<any>,
    nameTemplateRef: TemplateRef<any>,
  ): TableColumn[] {
    const nameColumn = new TableColumn(this.i18NextPipe.transform('HierarchyTemplateAttributeList.Name'), 'name');

    nameColumn.outputType = ColumnOutputType.Custom;
    nameColumn.customOutputTemplateRef = nameTemplateRef;

    const typeColumn = new TableColumn(this.i18NextPipe.transform('HierarchyTemplateAttributeList.Type'), 'dataType');

    typeColumn.outputType = ColumnOutputType.Custom;
    typeColumn.customOutputTemplateRef = typeTemplateRef;

    const uOMColumn = new TableColumn(this.i18NextPipe.transform('HierarchyTemplateAttributeList.Uom'), 'uom');

    uOMColumn.outputType = ColumnOutputType.Custom;
    uOMColumn.customOutputTemplateRef = uomTemplateRef;

    const lockedColumn = new TableColumn(this.i18NextPipe.transform('HierarchyTemplateAttributeList.Locked'), 'lockedAttributes');

    lockedColumn.outputType = ColumnOutputType.Custom;
    lockedColumn.customOutputTemplateRef = lockedTemplateRef;

    const indexColumn = new TableColumn('#', this.rowNumberColumnName);
    indexColumn.widthPx = defaultIndexColumnWidth;

    return [indexColumn, nameColumn, typeColumn, uOMColumn, lockedColumn];
  }

  private createBaseTableConfig(): TableConfig {
    const config = new TableConfig();

    config.enableDelete = false;
    config.enableFiltering = false;
    config.enablePagination = true;
    config.enableSorting = false;
    config.enableAdd = true;
    config.enableEdit = true;
    config.enableSelectionColumn = true;
    config.actions = [];

    return config;
  }

  public get defaultAttributeLoadEvent(): LazyLoadEvent {
    return {
      filters: {},
      first: 0,
      rows: RowsPerPage.Large,
      sortOrder: 1,
    };
  }

  public setTablePaginationConfig(recordsCount: number): void {
    this.attributesConfig.first = 0;
    this.attributesConfig.totalRecords = recordsCount;
    this.attributesConfig.rowsPerPage = RowsPerPage.Large;
  }

  public startLoading(config: TableConfig): void {
    this.tableConfigHelperService.modifyConfig(config, { loading: true });
  }

  public finishLoading(config: TableConfig): void {
    this.tableConfigHelperService.modifyConfig(config, { loading: false });
  }
}
